import { useParams } from 'react-router-dom';
import {
  Cradle,
  Recording,
  RecordingId,
  MusicalReleaseId,
  FileEntity,
  Language,
  ContributorRole,
} from '@solo/app-core';
import { useState, useEffect } from 'react';
import { Button, Flex, RouterLink, Section, Text } from '@/app/ui/atoms';
import { Icon } from '@/app/ui/molecules';
import { translate } from '@/app/i18n';
import DataInfo from '@/app/ui/organisms/DataInfo/DataInfo';
import TooltipInfo from '@/app/ui/organisms/TooltipInfo';
import { useCradle } from '@/app/contexts';
import { LoadingComponent } from '@/app/ui';
import { useBlocState } from '@/app/hooks';
import AudioPlayer from '@/app/ui/molecules/AudioPlayer/AudioPlayer';

const TrackDataComponent = () => {
  const { id, trackId } = useParams();
  const { musicalWorksBloc, revelatorDataBloc } = useCradle<Cradle>();
  const musicalWorksState = useBlocState(musicalWorksBloc);
  const revelatorDataState = useBlocState(revelatorDataBloc);
  const [track, setTrack] = useState<Recording>();
  const [totalMech, setTotalMech] = useState<number>();
  const [originalAuthors, setOriginalAuthors] = useState<(string | undefined)[]>();
  const [loading, setLoading] = useState<boolean>(true);
  const [audioInfo, setAudioInfo] = useState<FileEntity | undefined>();
  const [audioUrl, setAudioUrl] = useState<string>('');
  const [showFullLyrics, setShowFullLyrics] = useState<boolean>(false);
  const [language, setLanguage] = useState<Language | undefined>();
  const [authorComposerRoles, setAuthorComposerRoles] = useState<ContributorRole[]>([]);

  const getAudioInfo = async (t: Recording) => {
    if (!t?.audioId) return;

    const aInfo = await musicalWorksBloc.getAudioInfo(t.audioId);
    setAudioInfo(aInfo);
  };

  const getAudioUrl = async (t: Recording) => {
    if (!t?.audioId) return;

    const aUrl = await musicalWorksBloc.getAudioFile(t.audioId);
    setAudioUrl(URL.createObjectURL(aUrl));
  };

  const getLanguages = async () => {
    try {
      const l = await revelatorDataBloc.getLanguages();
      return l;
    } catch (error) {
      // console.error('Error getting languages', error);
      return [];
    }
  };

  useEffect(() => {
    const getRecording = async () => {
      if (id === undefined || trackId === undefined) {
        return;
      }

      const t = await musicalWorksBloc.getRecording(trackId as RecordingId, id as MusicalReleaseId);
      const l = await getLanguages();
      setLanguage(l.find((lang) => lang.id === t?.languageId));

      if (t === undefined) {
        return;
      }

      setTrack(t);
      getAudioInfo(t);
      getAudioUrl(t);

      if (t.type === 'ORIGINAL') {
        const { authors } = t;
        setTotalMech(
          authors
            .map((author) => author.mechanicalSplit || 0)
            .reduce((previous, current) => previous + current, 0),
        );
      }
      if (t.authors) setOriginalAuthors(t.authors.map((au) => au.authorComposerName));
    };

    getRecording();
  }, []);

  useEffect(() => {
    if (musicalWorksState.kind !== 'LoadedMusicalWorksState') {
      setLoading(true);
      return;
    }
    setLoading(false);
  }, [musicalWorksState]);

  useEffect(() => {
    if (revelatorDataState.kind === 'LoadedRevelatorDataState') {
      setAuthorComposerRoles(revelatorDataState.contributorRoles || []);
    }
  }, [revelatorDataState.kind]);

  const getRole = (roleId: number) =>
    authorComposerRoles.find((role) => role.id === roleId)?.name || '';

  if (loading) {
    return <LoadingComponent />;
  }

  return (
    <>
      <Section style={{ marginBottom: 24 }}>
        <RouterLink $transparent $variant="primary" to={`/works/${id}/tracks`}>
          <Icon.ArrowLeft width={10} />
          <Text.Paragraph fontWeight={600} style={{ marginLeft: 5 }}>
            {translate('WORKS_PAGES.BACK_TO_TRACKS')}
          </Text.Paragraph>
        </RouterLink>
      </Section>
      <Section style={{ alignItems: 'end', paddingBottom: 0, marginBottom: 32 }}>
        <Text.SectionTitle>{translate('WORKS_PAGES.TRACK_DETAILS')}</Text.SectionTitle>
      </Section>
      <Flex.Container flexDirection="column">
        <Flex.Container>
          <Flex.Item flex={1}>
            <DataInfo label={translate('TRACKS.TRACK_TITLE')} value={track?.title || ''} />
          </Flex.Item>
          <Flex.Item flex={1}>
            <DataInfo label={translate('TRACKS.COMPOSITION_TYPE')} value={track?.type || ''} />
          </Flex.Item>
        </Flex.Container>
        <Flex.Container>
          <Flex.Item flex={1}>
            <DataInfo
              label={translate('TRACKS.LANGUAGE_LYRICS')}
              value={language?.name || ''}
              infoTooltip="Lorem ipsum dolor secit"
            />
          </Flex.Item>
          <Flex.Item flex={1}>
            <DataInfo
              label={translate('TRACKS.EXPLICIT_CONTENT')}
              infoTooltip="Lorem ipsum dolor secit"
              value={track?.explicitContent ? translate('TRACKS.YES') : translate('TRACKS.NO')}
            />
          </Flex.Item>
        </Flex.Container>
        <Flex.Container>
          <Flex.Item flex={1}>
            <DataInfo
              label={translate('TRACKS.PROPERTIES')}
              value={
                track?.trackProperties
                  ?.map((property) => translate(`TRACK_PROPERTIES.${property}`))
                  .join(', ') || '-'
              }
            />
          </Flex.Item>
        </Flex.Container>
        <Flex.Container>
          <Flex.Item flex={1}>
            <DataInfo
              label={translate('TRACKS.ISRC')}
              infoTooltip="Lorem ipsum dolor secit"
              value={track?.isrc || ''}
            />
            <Flex.Container flexDirection="column">
              <Flex.Item flex={1}>
                <DataInfo
                  label={translate('TRACKS.AUDIO_FILE')}
                  infoTooltip="Lorem ipsum dolor secit"
                  value={audioInfo?.name || ''}
                />
              </Flex.Item>
              {audioUrl && (
                <Flex.Item flex={1}>
                  <AudioPlayer audioUrl={audioUrl} />
                </Flex.Item>
              )}
            </Flex.Container>
          </Flex.Item>
          <Flex.Item flex={1}>
            <Flex.Container flexDirection="column">
              <Flex.Item flex={1}>
                <DataInfo
                  label={translate('TRACKS.LYRICS')}
                  value={track?.lyrics || ''}
                  style={{ width: 400 }}
                  maxHeight={showFullLyrics ? 'none' : '130px'}
                />
              </Flex.Item>
              <Flex.Item flex={1}>
                <Button
                  $size="sm"
                  $transparent
                  $variant="primary"
                  style={{ width: '160px' }}
                  onClick={() => setShowFullLyrics(!showFullLyrics)}
                >
                  <Text.Span fontWeight={600}>
                    {translate(showFullLyrics ? 'WORKS_PAGES.SEE_LESS' : 'WORKS_PAGES.SEE_MORE')}
                  </Text.Span>
                </Button>
              </Flex.Item>
            </Flex.Container>
          </Flex.Item>
        </Flex.Container>
      </Flex.Container>

      <Section style={{ alignItems: 'end', paddingBottom: 0, marginBottom: 12 }} className="mt-10">
        <Flex.Container>
          <Text.SectionTitle className="mb-3">
            {translate('WORKS_PAGES.ARTIST_PERFORMER')}
          </Text.SectionTitle>
        </Flex.Container>
      </Section>
      <Flex.Container>
        <Flex.Item flex={1}>
          <DataInfo label={translate('TRACKS.ARTIST')} value={track?.artistName || ''} />
        </Flex.Item>
        <Flex.Item flex={1}>
          <DataInfo label={translate('TRACKS.ROLE')} value="Main Artist" />
        </Flex.Item>
      </Flex.Container>
      <Section style={{ alignItems: 'end', paddingBottom: 0, marginBottom: 12 }} className="mt-10">
        <Flex.Container>
          <Text.SectionTitle className="mb-3">
            {translate('WORKS_PAGES.AUTHOR_COMPOSERS')}
          </Text.SectionTitle>
          <TooltipInfo text="Lorem ipsum dolor secit" />
        </Flex.Container>
      </Section>
      {track?.type === 'ORIGINAL'
        ? track.authors?.map((authorWithComposer) => (
            <Flex.Container key={authorWithComposer.authorComposerId}>
              <Flex.Item flex={2}>
                <DataInfo
                  label={translate('TRACKS.ARTIST')}
                  value={authorWithComposer.authorComposerName || ''}
                />
              </Flex.Item>
              <Flex.Item flex={2}>
                <DataInfo label={translate('TRACKS.IPI')} value={authorWithComposer.ipi || ''} />
              </Flex.Item>
              <Flex.Item flex={2}>
                <DataInfo
                  label={translate('TRACKS.ROLE')}
                  value={getRole(authorWithComposer.roleId)}
                />
              </Flex.Item>
              <Flex.Item flex={1}>
                <DataInfo
                  label={translate('TRACKS.SHARES')}
                  value={`${authorWithComposer.mechanicalSplit} %`}
                />
              </Flex.Item>
            </Flex.Container>
          ))
        : originalAuthors?.map((author) => {
            if (author)
              return (
                <Flex.Container key={author}>
                  <Flex.Item flex={2}>
                    <DataInfo label={translate('TRACKS.ARTIST')} value={author} />
                  </Flex.Item>
                </Flex.Container>
              );
            return undefined;
          })}
      <Flex.Container>
        <Flex.Item flex={4} />
        <Flex.Item flex={2}>
          <Text.Label fontWeight={600} fontSize={16}>
            {translate('WORKS_PAGES.TOTAL')}
          </Text.Label>
        </Flex.Item>
        <Flex.Item flex={1}>
          <Text.Label fontWeight={600} fontSize={16}>{`${totalMech} %`}</Text.Label>
        </Flex.Item>
      </Flex.Container>
    </>
  );
};

export default TrackDataComponent;
